import { z } from 'zod';

// Fields common to both login providers.
const SharedEnvSchema = z.object({
    SFLX_ENVIRONMENT: z.string(),
    FRONTEND_MONOLITH_APP_VERSION: z.string(),
    CONSTRUCTION_PROJECT_BASE_URL: z.string(),
    MONOLITH_BASE_URL: z.string(),
    ORDER_SERVICE_BASE_URL: z.string(),
    ORDER_SERVICE_BASE_URL_V2: z.string(),
    FULFILLMENT_SERVICE_BASE_URL: z.string(),
    MANUAL_FULFILLMENT_SERVICE_BASE_URL: z.string(),
    POSITION_SERVICE_BASE_URL: z.string(),
    POSITION_SERVICE_BASE_URL_V2: z.string(),
    CUSTOM_REQUEST_SERVICE_BASE_URL: z.string(),
    DOCUMENT_MANAGEMENT_BRIDGE_BASE_URL: z.string().url(),
    USERFLOW_TOKEN: z.string().optional(),
    MONOLITH_FE_URL: z.string(),
    SENTRY_ENVIRONMENT: z.string(),
    SALESFORCE_DEAL_IMPORTER_SERVICE_URL: z.string(),
    SALESFORCE_HOST_URL: z.string(),
    LOCATION_SERVICE_URL: z.string(),
    GOOGLE_MAPS_API_KEY: z.string(),
    EXCLUDED_PARTNER_ORGANIZATION_IDS: z
        .string()
        .optional()
        .default('')
        .transform(value =>
            value
                .split(',')
                .map(entry => entry.trim())
                .filter(Boolean)
                .map(Number)
                .filter(id => Number.isInteger(id) && id > 0)
        ),
});

// The login provider selects which extra vars are required, so model it as a discriminated
// union: each provider declares exactly the credentials it needs. This keeps `OKTA_CLIENT_ID`
// required only for Okta and `OAUTH_ISSUER`/`OAUTH_CLIENT_ID` required only for OAuth, and lets
// consumers read them without empty-string fallbacks once they've narrowed on `AUTH_PROVIDER`.
const environmentSchema = z.preprocess(
    input => {
        const raw = (input ?? {}) as Record<string, unknown>;
        // `okta` (default) keeps the existing Okta flow; `oauth` uses the monolith OAuth server.
        return { ...raw, AUTH_PROVIDER: raw.AUTH_PROVIDER ?? 'okta' };
    },
    z
        .discriminatedUnion('AUTH_PROVIDER', [
            z.object({
                AUTH_PROVIDER: z.literal('okta'),
                OKTA_CLIENT_ID: z.string(),
            }),
            z.object({
                AUTH_PROVIDER: z.literal('oauth'),
                OAUTH_ISSUER: z.string().url(),
                OAUTH_CLIENT_ID: z.string(),
            }),
        ])
        .and(SharedEnvSchema)
);

export type Environment = z.infer<typeof environmentSchema>;

export const environment = environmentSchema.parse({
    SFLX_ENVIRONMENT: import.meta.env.VITE_SFLX_ENVIRONMENT,
    AUTH_PROVIDER: import.meta.env.VITE_AUTH_PROVIDER,
    OKTA_CLIENT_ID: import.meta.env.VITE_OKTA_CLIENT_ID,
    OAUTH_ISSUER: import.meta.env.VITE_OAUTH_ISSUER,
    OAUTH_CLIENT_ID: import.meta.env.VITE_OAUTH_CLIENT_ID,
    FRONTEND_MONOLITH_APP_VERSION: import.meta.env.VITE_FRONTEND_MONOLITH_APP_VERSION,
    CONSTRUCTION_PROJECT_BASE_URL: import.meta.env.VITE_CONSTRUCTION_PROJECT_SERVICE_URL,
    MONOLITH_BASE_URL: import.meta.env.VITE_MONOLITH_BASE_URL,
    ORDER_SERVICE_BASE_URL: import.meta.env.VITE_ORDER_SERVICE_URL,
    ORDER_SERVICE_BASE_URL_V2: import.meta.env.VITE_ORDER_SERVICE_URL_V2,
    FULFILLMENT_SERVICE_BASE_URL: import.meta.env.VITE_FULFILLMENT_SERVICE_URL,
    MANUAL_FULFILLMENT_SERVICE_BASE_URL: import.meta.env.VITE_MANUAL_FULFILLMENT_SERVICE_URL,
    POSITION_SERVICE_BASE_URL: import.meta.env.VITE_POSITION_SERVICE_URL,
    POSITION_SERVICE_BASE_URL_V2: import.meta.env.VITE_POSITION_SERVICE_V2_URL,
    CUSTOM_REQUEST_SERVICE_BASE_URL: import.meta.env.VITE_CUSTOM_REQUEST_SERVICE_URL,
    DOCUMENT_MANAGEMENT_BRIDGE_BASE_URL: import.meta.env.VITE_DOCUMENT_MANAGEMENT_BRIDGE_BASE_URL,
    USERFLOW_TOKEN: import.meta.env.VITE_USERFLOW_TOKEN,
    MONOLITH_FE_URL: import.meta.env.VITE_MONOLITH_FE_URL,
    SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    SALESFORCE_DEAL_IMPORTER_SERVICE_URL: import.meta.env.VITE_SALESFORCE_DEAL_IMPORTER_SERVICE_URL,
    SALESFORCE_HOST_URL: import.meta.env.VITE_SALESFORCE_HOST_URL,
    LOCATION_SERVICE_URL: import.meta.env.VITE_LOCATION_SERVICE_URL,
    GOOGLE_MAPS_API_KEY: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    EXCLUDED_PARTNER_ORGANIZATION_IDS: import.meta.env.VITE_EXCLUDED_PARTNER_ORGANIZATION_IDS,
});
